import React, { useEffect } from 'react';
import { MonitorPlay, Handshake, Scissors, Coins, CalendarDays, ShieldCheck, CheckCircle2, ArrowRight, Users, Sparkles } from 'lucide-react';

// Sahil partnership plan (v3, 2026-10-24). Replaces the paid-quote version (v2, ₹2.25L).
// Deal per WhatsApp 2026-10-19..24: 50-50 partners on a faceless Hindi business case-study
// channel, aim to monetize in 3 months (AdSense + products), plus clipping.
// Suresh decisions 2026-10-24: SocialSEO builds, Sahil funds running costs, profit 50-50
// after costs; niche = Hindi business case studies. No guarantees. No em dashes in copy.
// 2026-10-24 Suresh: Sahil puts in ₹1.5L for the 3 months (₹50K/month assumed) for running costs.

const SectionTag = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-300">
        <Icon size={14} /> {children}
    </div>
);

const StatCard = ({ n, label }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5">
        <div className="text-3xl sm:text-4xl font-black text-white">{n}</div>
        <div className="mt-1 text-sm text-slate-400">{label}</div>
    </div>
);

const Check = ({ children }) => (
    <li className="flex gap-3 text-slate-300"><CheckCircle2 size={18} className="shrink-0 text-emerald-400 mt-0.5" /><span>{children}</span></li>
);

const SIDES = [
    {
        who: 'SocialSEO brings', tone: 'border-indigo-400/30 from-indigo-500/15',
        items: ['Topic research from what is already pulling views', 'Scripts, Hindi AI voice, visuals and edit', 'Thumbnails, titles and descriptions', 'The clipping engine: Shorts and Reels from every video', 'Uploads, analytics and a weekly update'],
    },
    {
        who: 'Sahil brings', tone: 'border-emerald-400/30 from-emerald-500/15',
        items: ['The running costs: ₹1,50,000 for the 3 months, covering AI credits, voice and tools', 'Product ideas and the selling side, with your business network', 'Quick approvals on topics and scripts', 'Your take on which stories your audience wants'],
    },
];

const NICHES = [
    { t: 'Mahabharat, explained', b: 'The epic told as a story series, one character or one turning point per video, with what it means for life and work today. Viewers come back for the next part.', f: 'Long episodes as a series, plus clips of the big moments' },
    { t: 'Bhagavad Gita, every day', b: 'One shlok a day, read, explained in simple Hindi, and tied to one real problem people face today. A daily habit for the viewer and a daily post for the channel.', f: 'A short every day, plus a weekly long video' },
];

const MONEY = [
    { t: 'AdSense', b: 'YouTube pays once the channel joins the Partner Program: 1,000 subscribers plus 4,000 watch hours, or 1,000 subscribers plus 10 million Shorts views in 90 days. The clips help with both.' },
    { t: 'Products', b: 'We do not wait for AdSense. From month 2 we test a simple product for the same viewers, like a business case-study pack or a paid community.' },
    { t: 'Sponsors and affiliates', b: 'Once views are steady, brands that sell to business-minded viewers pay for mentions. Affiliate links go in the description from day one.' },
];

const PHASES = [
    { when: 'Week 1', title: 'Lock it', items: ['Channel name, logo and banner', 'Style locked from the two demos', 'First 10 topics picked from real view data'] },
    { when: 'Week 2', title: 'First videos live', items: ['First long videos up', 'Clips start on Shorts and Reels', 'We watch what holds viewers'] },
    { when: 'Month 1 to 3', title: 'Grow it', items: ['30 long videos over 3 months', 'Clips from every video, every day', 'Double down on the topics that win'] },
    { when: 'End of month 3', title: 'Review together', items: ['Where we are on the Partner Program', 'What the product test sold', 'Decide the next 3 months'] },
];

export default function SahilYouTubePlan() {
    useEffect(() => { document.title = 'SocialSEO | YouTube Partnership Plan'; }, []);

    return (
        <div className="min-h-screen text-white">
            <header className="fixed top-4 inset-x-0 z-50 flex justify-center px-4">
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur px-4 py-2 text-xs">
                    <span className="font-black">Social<span className="text-indigo-400">[SEO]</span></span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-300">For Sahil</span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-24 flex flex-col gap-24">

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Handshake}>Hey Sahil</SectionTag>
                    <h1 className="text-4xl sm:text-6xl font-black leading-[1.05] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        Let's build this channel together.
                    </h1>
                    <p className="text-lg text-slate-400 max-w-2xl">
                        50-50 partners on a faceless Hindi business case-study channel. We make the videos and the clips, you put in ₹1,50,000 for the running costs, and we aim to make money in 3 months from AdSense and our own products.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <StatCard n="50-50" label="partners on the profit" />
                        <StatCard n="30" label="long videos in 3 months" />
                        <StatCard n="Daily" label="clips on Shorts and Reels" />
                        <StatCard n="3" label="ways to earn" />
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={MonitorPlay}>The channel</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">Hindi business stories, told well.</h2>
                    <p className="text-slate-400 max-w-3xl">
                        The kind of videos you liked from HinExp and Money Code: how a brand rose, how it fell, and the money behind it. 8 to 10 minutes each, faceless, in the style we lock from the two demos you have already seen.
                    </p>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Sparkles}>Other niches we can run</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">Same engine, different stories.</h2>
                    <p className="text-slate-400 max-w-3xl">Business case studies are where we start. The same setup works for these too, as the first channel or a second one once the first is moving.</p>
                    <div className="grid md:grid-cols-2 gap-4">
                        {NICHES.map((n) => (
                            <div key={n.t} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-2">
                                <div className="text-lg font-bold">{n.t}</div>
                                <p className="text-sm text-slate-400 leading-relaxed">{n.b}</p>
                                <div className="text-xs text-indigo-300">{n.f}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Users}>Who brings what</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">Each side does what it is best at.</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {SIDES.map((s) => (
                            <div key={s.who} className={`rounded-2xl border ${s.tone} bg-gradient-to-b to-transparent p-6 flex flex-col gap-4`}>
                                <div className="text-lg font-bold">{s.who}</div>
                                <ul className="flex flex-col gap-3">{s.items.map((i) => <Check key={i}>{i}</Check>)}</ul>
                            </div>
                        ))}
                    </div>
                    <p className="text-slate-400 max-w-3xl">
                        Everything the channel earns, from AdSense, products, sponsors and affiliates, is split 50-50 after the running costs are paid back. The channel and everything on it belongs to both of us.
                    </p>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Scissors}>The clipping engine</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">One video, many shots at the feed.</h2>
                    <p className="text-slate-400 max-w-3xl">
                        Every long video becomes 4 to 5 short clips for YouTube Shorts and Instagram Reels. The clips pull new viewers into the long videos, and they count toward the Shorts route into the Partner Program. It is the same engine our team runs on pages like @backend.wala.
                    </p>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Coins}>How we make money</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">Three ways in, not one.</h2>
                    <div className="grid md:grid-cols-3 gap-4">
                        {MONEY.map((m) => (
                            <div key={m.t} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-2">
                                <div className="text-lg font-bold">{m.t}</div>
                                <p className="text-sm text-slate-400 leading-relaxed">{m.b}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={CalendarDays}>The 3 months</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">First videos live in week 2.</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {PHASES.map((p) => (
                            <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 flex flex-col gap-3">
                                <div className="text-xs font-bold uppercase tracking-widest text-indigo-300">{p.when}</div>
                                <div className="text-lg font-bold">{p.title}</div>
                                <ul className="flex flex-col gap-2 text-sm text-slate-400 list-disc pl-4">
                                    {p.items.map((i) => <li key={i}>{i}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={Coins}>Your side of the cost</SectionTag>
                    <div className="rounded-3xl border border-indigo-400/30 bg-gradient-to-br from-indigo-500/15 to-white/5 p-6 sm:p-10 grid md:grid-cols-2 gap-8">
                        <div className="flex flex-col gap-3">
                            <div className="text-sm text-indigo-200">For the full 3 months</div>
                            <div className="text-5xl sm:text-6xl font-black">₹1,50,000</div>
                            <div className="text-slate-400">₹50,000 at the start of each month. Our team and our time are our half of the partnership.</div>
                        </div>
                        <ul className="flex flex-col gap-3">
                            <Check>AI credits for every scene and motion clip</Check>
                            <Check>Hindi AI voice for every video</Check>
                            <Check>The tools behind visuals, edits and thumbnails</Check>
                            <Check>30 long videos plus the clips from each</Check>
                        </ul>
                    </div>
                    <p className="text-slate-400 max-w-3xl">Once the channel earns, these costs come back first, then everything is split 50-50.</p>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={ShieldCheck}>Keeping it clean</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">No AI spam, and everything in writing.</h2>
                    <p className="text-slate-400 max-w-3xl">
                        YouTube does not pay channels that churn out the same AI video again and again, so every video gets its own researched story, script and visuals, with the facts checked. And because we are partners, we put the split, the costs and how either of us can step out into a short written agreement before we start.
                    </p>
                </section>

                <section className="flex flex-col gap-6">
                    <SectionTag icon={ArrowRight}>To get going</SectionTag>
                    <h2 className="text-3xl sm:text-4xl font-black">Just 3 steps.</h2>
                    <ol className="grid sm:grid-cols-3 gap-4">
                        {[
                            ['Say yes to this plan', 'Or tell me what to change.'],
                            ['Sign a short agreement', 'The split, the costs, the exit. One page.'],
                            ['Send the first ₹50,000', 'We start the same week.'],
                        ].map(([t, b], i) => (
                            <li key={t} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 flex gap-4">
                                <div className="shrink-0 h-9 w-9 rounded-full bg-indigo-500 text-white font-black flex items-center justify-center">{i + 1}</div>
                                <div className="min-w-0"><div className="font-bold">{t}</div><div className="text-sm text-slate-400 mt-1">{b}</div></div>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="rounded-3xl border border-white/10 bg-white/5 p-8 sm:p-12 text-center flex flex-col items-center gap-3">
                    <h2 className="text-3xl sm:text-4xl font-black">Let's build it.</h2>
                    <p className="text-slate-400 max-w-xl">Reply on WhatsApp whenever you're ready.</p>
                    <p className="text-slate-500 text-sm">Suresh</p>
                </section>
            </main>
        </div>
    );
}
